import { ModificationGroupFragment } from './api/__queries__';

export interface Variation {
  id: string;
  name: string;
  selected: boolean;
  price: number;
  imageUrl: string | undefined;
  asItem: {
    id: string;
  } | null;
  category: {
    id: string;
    name: string;
  } | null;
  isOutOfStock?: boolean;
}

interface FormatVariationsParams {
  variations: any[];
  isSelected: (id: string) => boolean;
  isPreselectedInAny: (id: string) => boolean;
  itemCategoryMap: Record<string, { id: string; name: string }>;
  itemIsOutOfStock: (id: string) => boolean;
  i18n: (nameLang: any) => string;
}

export const formatVariations = ({
  variations,
  isSelected,
  isPreselectedInAny,
  itemCategoryMap,
  itemIsOutOfStock,
  i18n,
}: FormatVariationsParams): Variation[] => {
  let atLeastOneSelected = false;

  return variations.map((vari) => {
    const selected = isSelected(vari.id);

    // Inverse selection set for default variations.
    const markAsSelected = isPreselectedInAny(vari.id) ? !selected : selected;

    atLeastOneSelected = markAsSelected || atLeastOneSelected;

    // Check if the variation is connected to an item, display its category
    const category = vari.asItem ? itemCategoryMap[vari.asItem.id] : null;

    // Check inventory status
    const isOutOfStock = itemIsOutOfStock(vari.id);

    const variName = i18n(vari.nameLang);

    return {
      id: vari.id,
      name: variName.length > 0 ? variName : vari.name,
      price: vari.price,
      asItem: vari.asItem
        ? {
            id: vari.asItem.id,
            categories: [itemCategoryMap[vari.asItem.id]],
          }
        : null,
      selected: !isOutOfStock && markAsSelected,
      imageUrl: vari.image?.file.url ?? null,
      isOutOfStock,
      category,
    };
  });
};

/**
 * Normally, only ONE variation is preselected per group.
 * However, for removable ingredients, all variations are preselected.
 * We use this behavior to check if the group is a removable-ingredients group
 * by comparing the length of preselectedVariations with variations.
 * If all are preselected, we skip adding them (handled separately elsewhere).
 */
interface ModificationGroupForPopupCheck {
  expandedByDefault: boolean;
  required: boolean;
  preselectedVariations: string[];
  variations: { id: string }[];
}

/**
 * Do these modification groups have variations that should trigger the
 * ItemModifications popup instead of adding the item straight away?
 */
export const shouldShowModificationPopup = (
  modificationGroups: ModificationGroupForPopupCheck[],
): boolean =>
  modificationGroups
    .filter((mg) => mg.expandedByDefault || (mg.required && mg.preselectedVariations.length === 0))
    .flatMap((mg) => mg.variations).length > 0;

export const getInitiallySelectedVariations = (groups: ModificationGroupFragment[]) => {
  return groups.reduce((acc: string[], group) => {
    if (
      group.preselectedVariations.length === group.variations.length &&
      group.type !== 'SINGLE'
    ) {
      return [...acc];
    }

    return [...acc, group.preselectedVariations[0]];
  }, []);
};
